/**
 * avatarCompressor - клиентское сжатие и кадрирование аватарок через HTML5 Canvas.
 * Масштабирует изображение до 512x512 px, кадрирует в пропорции 1:1 по центру
 * и сжимает в формат WebP (~25-45 КБ вместо исходных 5-15 МБ).
 */

export interface CompressAvatarOptions {
  targetSize?: number; // Разрешение квадрата (по умолчанию 512px)
  quality?: number;    // Качество сжатия WebP (0.1 - 1.0, по умолчанию 0.85)
}

/**
 * Сжимает переданный файл изображения и возвращает Base64 Data URL.
 */
export async function compressAvatar(
  file: File,
  options: CompressAvatarOptions = {}
): Promise<string> {
  const { targetSize = 512, quality = 0.85 } = options;

  return new Promise((resolve, reject) => {
    // Проверка типа файла
    if (!file.type.startsWith('image/')) {
      reject(new Error('Выбранный файл не является изображением'));
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      try {
        const canvas = document.createElement('canvas');
        canvas.width = targetSize;
        canvas.height = targetSize;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Не удалось инициализировать 2D Canvas'));
          return;
        }

        // Включаем качественное сглаживание
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Центрированное кадрирование в квадрат (cover)
        const minDim = Math.min(img.naturalWidth, img.naturalHeight);
        const sx = (img.naturalWidth - minDim) / 2;
        const sy = (img.naturalHeight - minDim) / 2;

        ctx.drawImage(
          img,
          sx,
          sy,
          minDim,
          minDim,
          0,
          0,
          targetSize,
          targetSize
        );

        // Экспорт в WebP с качеством 0.82 (если браузер не поддерживает, toDataURL вернет image/png)
        let dataUrl = canvas.toDataURL('image/webp', quality);

        // Если размер по-прежнему велик или toDataURL вернул PNG, пробуем JPEG
        if (dataUrl.startsWith('data:image/png')) {
          dataUrl = canvas.toDataURL('image/jpeg', quality);
        }

        resolve(dataUrl);
      } catch (err) {
        reject(err);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Ошибка чтения изображения'));
    };

    img.src = objectUrl;
  });
}

/**
 * Вычисляет примерный размер Data URL в килобайтах.
 */
export function getDataUrlSizeKB(dataUrl: string): number {
  if (!dataUrl) return 0;
  const base64Length = dataUrl.length - (dataUrl.indexOf(',') + 1);
  const bytes = (base64Length * 3) / 4;
  return Math.round(bytes / 1024);
}
